import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { FiCamera, FiCheck, FiMail, FiMinus, FiPhone } from "react-icons/fi";
import { LuArrowDown, LuArrowUp, LuArrowUpDown } from "react-icons/lu";
import { isUrdu } from "../../../constants/const";
import { BOOLEAN_COLUMNS, DATE_COLUMNS, NEW_COLUMNS, SORTABLE_COLUMNS, columnLabelKey } from "./reportColumns";
import MemberPhotoModal from "./MemberPhotoModal";

const URDU_COLUMNS = ["name", "fatherName", "halqa", "tanzeem", "usra", "nazam", "country", "city", "arabicTest", "tajweedTest", "educationDiscipline", "occupation", "experience"];
const ADDRESS_COLUMNS = ["currentAddress", "permanentAddress", "nativeTown"];
const LIST_COLUMNS = ["education", "educationDiscipline", "occupation", "experience", "skill"];

export const formatDate = (value) => {
  if (!value) return "";
  const [y, m, d] = String(value).substring(0, 10).split("-");
  return d && m && y ? `${d}/${m}/${y}` : String(value);
};

export const formatCnic = (cnic) => {
  if (!cnic) return "";
  const digits = String(cnic).replace(/\D/g, "");
  if (digits.length === 13) {
    return `${digits.slice(0, 5)}-${digits.slice(5, 12)}-${digits.slice(12)}`;
  }
  return cnic;
};

const Empty = () => <span className="mr-muted">—</span>;

const splitList = (value) =>
  (value || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);

const SortIcon = ({ state }) =>
  state === "ascending" ? (
    <LuArrowUp size={13} className="mr-sort-icon" />
  ) : state === "descending" ? (
    <LuArrowDown size={13} className="mr-sort-icon" />
  ) : (
    <LuArrowUpDown size={13} className="mr-sort-icon" />
  );

const Cell = ({ record, column, memberTypeTones, t, onShowPhoto }) => {
  const value = record[column];

  if (BOOLEAN_COLUMNS.includes(column))
    return value ? (
      <span className="mr-yn mr-yes">
        <FiCheck size={13} />
      </span>
    ) : (
      <span className="mr-yn mr-no">
        <FiMinus size={13} />
      </span>
    );

  if (DATE_COLUMNS.includes(column)) return value ? <span className="en">{formatDate(value)}</span> : <Empty />;

  switch (column) {
    case "registrationNumber":
      return value ? <span className="en mr-reg">{value}</span> : <Empty />;
    case "name":
      return (
        <>
          {value}
          {record.isDied && <span className="mr-tag mr-tag-died">{t("member_is_died")}</span>}
        </>
      );
    case "memberType":
      return value ? (
        <span className={`mr-pill mr-pill-${memberTypeTones[value] || "slate"}` + (isUrdu(value) ? " ur" : "")}>
          {value}
        </span>
      ) : (
        <Empty />
      );
    case "responsbilities": {
      const items = splitList(value);
      return items.length ? (
        <span className="mr-pills">
          {items.map((item, i) => (
            <span key={i} className={"mr-pill" + (isUrdu(item) ? " ur" : "") + " mr-pill-indigo"}>
              {item}
            </span>
          ))}
        </span>
      ) : (
        <Empty />
      );
    }
    case "phone":
    case "mobile":
      return value ? (
        <a href={`tel:${value}`} className="mr-link en" onClick={(e) => e.stopPropagation()}>
          <FiPhone size={12} /> {value}
        </a>
      ) : (
        <Empty />
      );
    case "email":
      return value ? (
        <a href={`mailto:${value}`} className="mr-link en" onClick={(e) => e.stopPropagation()}>
          <FiMail size={12} /> {value}
        </a>
      ) : (
        <Empty />
      );
    case "phoneList":
    case "mobileList":
    case "emailList": {
      const items = splitList(value);
      return items.length ? (
        <span className="mr-lines en">
          {items.map((item, i) => (
            <span key={i}>
              {item.includes("@") ? <FiMail size={12} /> : <FiPhone size={12} />}
              {item}
            </span>
          ))}
        </span>
      ) : (
        <Empty />
      );
    }
    case "cnic":
      return value ? <span className="en mr-reg">{formatCnic(value)}</span> : <Empty />;
    case "photo":
      return value ? (
        <button
          type="button"
          className="mr-photo-btn"
          title={t("member_photo")}
          onClick={(e) => {
            e.stopPropagation();
            onShowPhoto?.(record);
          }}
          aria-label={t("member_photo")}
        >
          <FiCamera size={14} className="mr-photo-icon-animated" />
        </button>
      ) : (
        <span className="mr-yn mr-no">
          <FiMinus size={13} />
        </span>
      );
    case "maritalStatus":
      return value ? <span className={"mr-pill mr-pill-violet" + (isUrdu(value) ? " ur" : "")}>{value}</span> : <Empty />;
    case "childrenDetails":
      return record.sons === null && record.daughters === null ? (
        <Empty />
      ) : (
        <span className="mr-kids">
          <span className="mr-sons">
            {t("member_sons")} <b className="en">{record.sons ?? 0}</b>
          </span>
          <span className="mr-daughters">
            {t("member_daughters")} <b className="en">{record.daughters ?? 0}</b>
          </span>
        </span>
      );
    default:
      break;
  }

  if (ADDRESS_COLUMNS.includes(column))
    return value ? (
      <span className={"mr-trunc" + (isUrdu(value) ? " ur" : "")} title={value}>
        {value}
      </span>
    ) : (
      <Empty />
    );

  if (LIST_COLUMNS.includes(column)) {
    const items = splitList(value);
    return items.length ? (
      <span className="mr-lines">
        {items.map((item, i) => (
          <span key={i} className={isUrdu(item) ? "ur" : ""}>
            {item}
          </span>
        ))}
      </span>
    ) : (
      <Empty />
    );
  }

  return value === null || value === undefined || value === "" ? <Empty /> : value;
};

const ReportTable = ({ rows, columns, sortObject, onSort, selectedIds, onToggleRow, onToggleAll }) => {
  const { t } = useTranslation();
  const pinName = columns.includes("name");
  const [photoMember, setPhotoMember] = useState(null);

  // Colour member types by how common they are in these results.
  const memberTypeTones = React.useMemo(() => {
    const counts = {};
    rows.forEach((r) => { if (r.memberType) counts[r.memberType] = (counts[r.memberType] || 0) + 1; });
    const tones = ["teal", "sky", "amber", "violet", "rose", "indigo"];
    return Object.keys(counts)
      .sort((a, b) => counts[b] - counts[a])
      .reduce((map, type, i) => ({ ...map, [type]: tones[i % tones.length] }), {});
  }, [rows]);

  const selectedVisible = rows.filter((r) => selectedIds.has(r.id)).length;
  const allSelected = rows.length > 0 && selectedVisible === rows.length;

  return (
    <div className="mr-tbl-wrap">
      <table className="mr-table">
        <thead>
          <tr>
            <th className="mr-stick1 noprint">
              <input
                type="checkbox"
                aria-label={t("member_report_select_all")}
                checked={allSelected}
                ref={(el) => el && (el.indeterminate = selectedVisible > 0 && !allSelected)}
                onChange={() => onToggleAll(!allSelected)}
              />
            </th>
            <th className="mr-stick2">#</th>
            {columns.map((column) => {
              const sortable = SORTABLE_COLUMNS.includes(column);
              const state = sortObject[column];
              const classes = [
                column === "name" && pinName ? "mr-stick3" : "",
                sortable ? "mr-sortable" : "",
                sortable && state && state !== "neutral" ? "mr-sorted" : "",
              ].join(" ");
              return (
                <th key={column} className={classes} onClick={sortable ? () => onSort(column) : undefined}>
                  {t(columnLabelKey(column))}
                  {NEW_COLUMNS.includes(column) && <span className="mr-new noprint">{t("member_report_new")}</span>}
                  {sortable && (
                    <span className="noprint">
                      <SortIcon state={state} />
                    </span>
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((record, index) => {
            const selected = selectedIds.has(record.id);
            return (
              <tr key={record.id} className={selected ? "mr-selected" : ""}>
                <td className="mr-stick1 noprint">
                  <input
                    type="checkbox"
                    aria-label={t("member_report_select_row")}
                    checked={selected}
                    onChange={() => onToggleRow(record.id)}
                  />
                </td>
                <td className="mr-stick2">{index + 1}</td>
                {columns.map((column) => (
                  <td
                    key={column}
                    className={[
                      column === "name" && pinName ? "mr-stick3 mr-name" : "",
                      URDU_COLUMNS.includes(column) && isUrdu(String(record[column] ?? "")) ? "ur" : "",
                    ].join(" ")}
                  >
                    <Cell record={record} column={column} memberTypeTones={memberTypeTones} t={t} onShowPhoto={setPhotoMember} />
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {photoMember && (
        <MemberPhotoModal
          isOpen={!!photoMember}
          onClose={() => setPhotoMember(null)}
          member={photoMember}
        />
      )}
    </div>
  );
};

export default ReportTable;
